import type { FC } from 'react';
import React, { useContext, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Stack, Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Image from '@atlaskit/image';
import { cssMap } from '@atlaskit/css';
import Button from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { ErrorDisplay } from '@confluence/error-boundary';
import { ExperienceTrackerContext, AI_AUDIO_BRIEFING } from '@confluence/experience-tracker';
import { ConfluenceLabsLozenge } from '@confluence/ai-common';

import AudioBriefingErrorIllustration from '../../assets/AudioBriefingErrorIllustration.svg';
import AudioBriefingErrorIllustrationDark from '../../assets/AudioBriefingErrorIllustrationDark.svg';
import { useAudioBriefing } from '../../hooks/useAudioBriefing';
import { getSharedAttributes } from '../../utils/getSharedAttributes';
import { AudioBriefingError } from '../../types/audioBriefingErrorType';

import { AudioBriefingPopupControlButton } from './AudioBriefingPopupControlButton';

const styles = cssMap({
	wrapper: {
		width: '300px',
	},
	errorIllustration: {
		width: '84px',
		height: '84px',
	},
	errorIllustrationContainer: {
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.150'),
		paddingBlockEnd: token('space.100'),
	},
	errorTextContainer: {
		textAlign: 'center',
		whiteSpace: 'pre-line',
		paddingInline: token('space.300'),
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.200'),
	},
	retryButtonContainer: {
		paddingBlockEnd: token('space.150'),
	},
	header: {
		marginBottom: token('space.075'),
	},
});

const i18n = defineMessages({
	errorMessage: {
		id: 'audio.audio-briefing-popup.error.message',
		defaultMessage: 'We couldn’t load the audio for this briefing.',
		description: 'Error message for the audio briefings popup',
	},
	contentTooShortErrorMessage: {
		id: 'audio.audio-briefing-popup.error.content-too-short-error-message',
		defaultMessage: 'Not enough info to create a briefing',
		description: 'Error message for the audio briefings popup when the content is too short',
	},
	errorImgAlt: {
		id: 'audio.audio-briefing-popup.error.img.alt',
		defaultMessage: 'Audio briefings popup error illustration',
		description: 'Alt text for the audio briefings popup error illustration',
	},
	tryAgainButtonLabel: {
		id: 'audio.audio-briefing-popup.error.try-again',
		defaultMessage: 'Try again',
		description: 'Text for button that allows users to try loading the briefing again',
	},
});

type AudioBriefingsErrorComponentProps = {
	error: Error;
	audioRef: React.RefObject<HTMLAudioElement>;
	closePopup: () => void;
};

export const AudioBriefingErrorComponent: FC<AudioBriefingsErrorComponentProps> = ({
	error,
	audioRef,
	closePopup,
}) => {
	const { formatMessage } = useIntl();
	const [
		{
			activeContent,
			errorType,
			customization,
			additionalResources,
			playbackSpeed,
			hasFetched,
			lastGeneratedBriefingConfig,
		},
		{ fetchBriefing },
	] = useAudioBriefing();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { trackAIResultError, trackAIInteractionInit } = useAIEventsInstrumentation();

	useEffect(() => {
		experienceTracker.fail({ name: AI_AUDIO_BRIEFING, error });
		trackAIResultError(
			{
				aiErrorMessage: error.message,
			},
			{
				attributes: getSharedAttributes({
					customization: lastGeneratedBriefingConfig?.customization,
					additionalResources: lastGeneratedBriefingConfig?.additionalResources,
					playbackSpeed,
					hasFetched,
				}),
			},
		);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleRetry = async () => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}

		experienceTracker.start({
			name: AI_AUDIO_BRIEFING,
			attributes: {
				contentId: activeContent?.id,
			},
		});

		trackAIInteractionInit({
			attributes: getSharedAttributes({
				customization,
				additionalResources,
				playbackSpeed,
				hasFetched,
			}),
		});

		fetchBriefing();
	};

	const errorMessage =
		errorType === AudioBriefingError.CONTENT_TOO_SHORT
			? formatMessage(i18n.contentTooShortErrorMessage)
			: formatMessage(i18n.errorMessage);

	return (
		<Box xcss={styles.wrapper} padding="space.200">
			<Inline spread="space-between" alignBlock="center" xcss={styles.header}>
				<ConfluenceLabsLozenge />
				<AudioBriefingPopupControlButton closePopup={closePopup} />
			</Inline>
			<Stack alignInline="center">
				<Box xcss={styles.errorIllustrationContainer}>
					<Inline xcss={styles.errorIllustration} alignInline="center" alignBlock="center">
						<Image
							src={AudioBriefingErrorIllustration}
							alt={formatMessage(i18n.errorImgAlt)}
							srcDark={AudioBriefingErrorIllustrationDark}
						/>
					</Inline>
				</Box>

				<Box xcss={styles.errorTextContainer}>
					<Heading size="small">{errorMessage}</Heading>
				</Box>

				{errorType !== AudioBriefingError.CONTENT_TOO_SHORT && (
					<Box xcss={styles.retryButtonContainer}>
						<Button onClick={handleRetry}>
							<Text>{formatMessage(i18n.tryAgainButtonLabel)}</Text>
						</Button>
					</Box>
				)}

				<ErrorDisplay error={error} />
			</Stack>
		</Box>
	);
};
